import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

import type {
  InterviewDeliveryMode,
  StudyLanguage,
} from '@/api/core-api/types.gen';

import { KindOfResearch, ResearchConductType } from '@/api/enums';
import { organizationCanUseAiQueryOptions } from '@/hooks/api/use-organization';

import type { ProjectCreateNavState } from '../-project-create-nav-state';

import {
  DEFAULT_INTERVIEW_MEDIUM,
  DEFAULT_STUDY_LANGUAGE,
} from './ai-interview-settings';

export function buildAiInhouseNavState(
  medium: InterviewDeliveryMode = DEFAULT_INTERVIEW_MEDIUM,
  language: StudyLanguage = DEFAULT_STUDY_LANGUAGE,
  responseLanguages: StudyLanguage[] = [],
): ProjectCreateNavState {
  return {
    platform: 'ai-inhouse',
    kindOfResearch: KindOfResearch.AI_MODERATED_INTERVIEW,
    researchConductType: ResearchConductType.REMOTE,
    interviewDeliveryMode: medium,
    studyLanguage: language,
    ...(responseLanguages.length > 0 ? { responseLanguages } : {}),
  };
}

/**
 * Where entering project creation lands for the given setup state.
 *
 * The prompt page (free-text prompt, brief upload) is itself an AI entry
 * point, so it is only the destination while the organization may use AI
 * (`canUseAi`). Otherwise — opted out, but also an unresolved or failed
 * organization read, or no organization — creation fails closed into the
 * manual editor with the same study configuration: the setup choices (study
 * type, format, languages, program) are kept, only the AI prompt step is
 * removed. The prompt route's `beforeLoad` applies this same decision, so
 * direct navigation cannot reach the prompt page either.
 */
export function resolveProjectCreationDestination(
  state: ProjectCreateNavState,
  canUseAi: boolean,
):
  | { to: '/researchers/projects/prompt'; state: ProjectCreateNavState }
  | {
      to: '/researchers/projects/$projectId/edit';
      params: { projectId: 'new' };
      state: ProjectCreateNavState;
    } {
  if (!canUseAi) {
    return {
      to: '/researchers/projects/$projectId/edit',
      params: { projectId: 'new' },
      state: { ...state, prompt: undefined },
    };
  }
  return { to: '/researchers/projects/prompt', state };
}

/**
 * Navigation into the project creation flow (the prompt page).
 *
 * Single source of truth for entering setup — used by the new-project setup
 * modal (AI-moderated and external paths) and the AI Moderator explainer —
 * so every entry point lands on the same route with consistent history
 * behavior. Build the nav state with `buildAiInhouseNavState` (or inline for
 * the external path) and hand it here. For an organization that opted out of
 * AI the destination is the manual editor instead (see
 * `resolveProjectCreationDestination`); the prompt route's `beforeLoad`
 * enforces the same rule for direct navigation.
 */
export function useStartProjectCreation(organizationId: Maybe<string>) {
  const navigate = useNavigate();
  const { data: canUseAi = false } = useQuery(
    organizationCanUseAiQueryOptions(organizationId),
  );

  return useCallback(
    (state: ProjectCreateNavState) => {
      const destination = resolveProjectCreationDestination(state, canUseAi);
      if (destination.to === '/researchers/projects/prompt') {
        void navigate({
          to: destination.to,
          replace: true,
          state: destination.state as Record<string, unknown>,
        });
        return;
      }
      void navigate({
        to: destination.to,
        params: destination.params,
        replace: true,
        state: destination.state as Record<string, unknown>,
      });
    },
    [navigate, canUseAi],
  );
}
